import { useMutation } from '@apollo/client'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import Dropdown from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import IconButton from 'components/v2/IconButton'
import List from 'components/v2/List'
import { AttachmentFileIcon } from 'components/Icons/AttachmentFileIcon'
import { IPaymentSummaryModel } from 'features/PaymentsSummary/types'
import { SEND_PARTNERS_PAYMENT_SUMMARY_EMAILS } from 'features/PaymentsSummary/mutations'
import { WarningIcon } from 'components/Icons'
import { PartnersEmailSendNotification } from 'features/PaymentsSummary/components/PartnersEmailSendNotification'
import { SendIcon } from 'components/Icons/SendIcon'
import { updatePartnersPaymentSummaryStatus } from 'features/PaymentsSummary/cache'
import { CreatePartnersPaymentSummaryCategoryInput } from 'gql/generated/graphql'
import { isEmpty } from 'lodash'

const StyledWarningIcon = styled(WarningIcon)`
  fill: #ff627b;
  margin-left: 10px;
`

interface IPaymentsSummaryListActionsProps {
  paymentSummary: IPaymentSummaryModel
}

export const PaymentsSummaryListActions = ({ paymentSummary }: IPaymentsSummaryListActionsProps): ReactElement => {
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [showEmailNotification, setShowEmailNotification] = useState(false)

  const [sendPartnersPaymentSummaryEmails, { loading: sendEmailsLoading, error: sendEmailsError }] = useMutation(
    SEND_PARTNERS_PAYMENT_SUMMARY_EMAILS,
    {
      onCompleted: (): void => {
        updatePartnersPaymentSummaryStatus(paymentSummary.id, 'SENT')
        setShowEmailNotification(true)
      },
      onError: (): void => undefined
    }
  )

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  const handleSendEmails = (): void => {
    setShowEmailNotification(false)

    sendPartnersPaymentSummaryEmails({
      variables: {
        input: {
          id: paymentSummary.id
        }
      }
    })
  }

  const isEnabled = (): boolean => {
    if (paymentSummary.status === 'READY') return true

    if (paymentSummary.status === 'ERRORS') {
      const files = paymentSummary.files.filter((file) => {
        return file.category !== CreatePartnersPaymentSummaryCategoryInput.Tag
      })
      const hasValidFiles = files.every((file) => isEmpty(file.errorPath))

      return hasValidFiles
    }

    return false
  }

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): void => setOpenDropdown(false)}>
        <IconButton icon={<DotsIcon />} noBorder smallPadding onClick={toggleDropdown} />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.ItemLink href={`/partners/payment-summary/${paymentSummary.id}`}>
              <List.Icon>
                <AttachmentFileIcon />
              </List.Icon>
              <FormattedMessage id="paymentSummary.list.edit" defaultMessage="Edit attachment" />
            </List.ItemLink>
            <List.Item disabled={!isEnabled()} loading={sendEmailsLoading} onClick={handleSendEmails} action>
              <List.Icon>
                <SendIcon />
              </List.Icon>
              <FormattedMessage id="paymentSummary.list.send" defaultMessage="Send email" />
              {sendEmailsError && <StyledWarningIcon />}
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {showEmailNotification && <PartnersEmailSendNotification onClose={(): void => setShowEmailNotification(false)} />}
    </>
  )
}
